import { DEFAULT_PRODUCT_BRAND_NAME } from './product-branding';

const DEFAULT_REMEDIATION_BRAND_LABEL = 'Terra';
const DEFAULT_REMEDIATION_BRANCH_NAMESPACE = 'terra';

export const REMEDIATION_BRANCH_PREFIX = `${DEFAULT_REMEDIATION_BRANCH_NAMESPACE}/remediate-`;

export function remediationBrandLabel(brandName?: string | null): string {
  const trimmed = brandName?.trim() ?? '';

  if (
    trimmed.length === 0 ||
    trimmed.localeCompare(DEFAULT_PRODUCT_BRAND_NAME, undefined, {
      sensitivity: 'accent',
    }) === 0
  ) {
    return DEFAULT_REMEDIATION_BRAND_LABEL;
  }

  return trimmed;
}

function remediationBranchNamespace(brandName?: string | null): string {
  const slug = sanitizeBranchNameSlug(remediationBrandLabel(brandName));

  return slug || DEFAULT_REMEDIATION_BRANCH_NAMESPACE;
}

export const MAX_REMEDIATION_BRANCH_NAME_LENGTH = 100;
export const MAX_REMEDIATION_PR_TITLE_LENGTH = 256;
const TITLE_SLUG_MAX_LENGTH = 50;

const SUPPORTED_REMEDIATION_TICKET_KEY_PROVIDERS = new Set(['jira', 'linear']);

export const LINKED_TICKET_GROUP_PROVIDERS = [
  'jira',
  'linear',
  'servicenow',
] as const;

export type LinkedTicketGroupProvider =
  (typeof LINKED_TICKET_GROUP_PROVIDERS)[number];

const LINKED_TICKET_PROVIDERS: ReadonlySet<string> = new Set(
  LINKED_TICKET_GROUP_PROVIDERS,
);

export type RemediationExternalReferenceLike = {
  provider: string;
  key?: string | null;
};

export type LinkedTicketExternalReferenceLike = {
  provider: string;
  key?: string | null;
  url?: string | null;
};

export function sanitizeBranchNameSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, TITLE_SLUG_MAX_LENGTH);
}

export function sanitizeUserProvidedBranchName(branchName: string): string {
  return branchName
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[~^:?*[\]\\@{}]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/\/{2,}/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .substring(0, MAX_REMEDIATION_BRANCH_NAME_LENGTH);
}

export function resolveLinkedTicketKey(
  externalReferences: ReadonlyArray<RemediationExternalReferenceLike>,
): string | null {
  const match = externalReferences.find((reference) => {
    if (!SUPPORTED_REMEDIATION_TICKET_KEY_PROVIDERS.has(reference.provider)) {
      return false;
    }

    return typeof reference.key === 'string' && reference.key.trim().length > 0;
  });

  return match?.key?.trim() ?? null;
}

export function resolveLinkedTicketReference(
  externalReferences: ReadonlyArray<LinkedTicketExternalReferenceLike>,
): LinkedTicketExternalReferenceLike | null {
  return (
    externalReferences.find(
      (reference) =>
        LINKED_TICKET_PROVIDERS.has(reference.provider) &&
        !!reference.url?.trim(),
    ) ?? null
  );
}

export function resolveLinkedTicketGroupKey(
  externalReferences: ReadonlyArray<LinkedTicketExternalReferenceLike>,
): string {
  const ticket = resolveLinkedTicketReference(externalReferences);

  if (!ticket) {
    return '';
  }

  return ticket.key?.trim() || ticket.url?.trim() || '';
}

export function buildDefaultRemediationBranchName({
  findingTitle,
  ticketKey,
  brandName,
  timestamp = Date.now(),
}: {
  findingTitle: string;
  ticketKey?: string | null;
  brandName?: string | null;
  timestamp?: number;
}): string {
  const slug = sanitizeBranchNameSlug(findingTitle);
  const sanitizedTicketKey = ticketKey ? sanitizeBranchNameSlug(ticketKey) : '';
  const namespace = remediationBranchNamespace(brandName);
  const prefix = sanitizedTicketKey
    ? `${namespace}/${sanitizedTicketKey}-remediate-`
    : `${namespace}/remediate-`;

  const branchName = `${prefix}${slug}-${timestamp}`;

  if (branchName.length <= MAX_REMEDIATION_BRANCH_NAME_LENGTH) {
    return branchName;
  }

  const maxSlugLength =
    MAX_REMEDIATION_BRANCH_NAME_LENGTH -
    prefix.length -
    String(timestamp).length -
    1;

  return `${prefix}${slug.substring(0, Math.max(0, maxSlugLength))}-${timestamp}`;
}

export function buildDefaultRemediationPrTitle({
  findingTitle,
  ticketKey,
  brandName,
}: {
  findingTitle: string;
  ticketKey?: string | null;
  brandName?: string | null;
}): string {
  const resolvedBrand = remediationBrandLabel(brandName);
  const prefix = `[${resolvedBrand}] Security Fix: `;
  const trimmedTicketKey = ticketKey?.trim();
  const suffix = trimmedTicketKey ? ` (${trimmedTicketKey})` : '';
  const maxFindingTitleLength = Math.max(
    0,
    MAX_REMEDIATION_PR_TITLE_LENGTH - prefix.length - suffix.length,
  );

  return `${prefix}${findingTitle.substring(0, maxFindingTitleLength)}${suffix}`.substring(
    0,
    MAX_REMEDIATION_PR_TITLE_LENGTH,
  );
}

export function resolveRemediationPrNaming({
  findingTitle,
  externalReferences = [],
  branchName,
  prTitle,
  brandName,
  timestamp = Date.now(),
}: {
  findingTitle: string;
  externalReferences?: ReadonlyArray<RemediationExternalReferenceLike>;
  branchName?: string | null;
  prTitle?: string | null;
  brandName?: string | null;
  timestamp?: number;
}): { branchName: string; prTitle: string } {
  const ticketKey = resolveLinkedTicketKey(externalReferences);
  const trimmedTitle = prTitle?.trim() ?? '';
  const resolvedTitle =
    trimmedTitle.length > 0
      ? trimmedTitle
      : buildDefaultRemediationPrTitle({ findingTitle, ticketKey, brandName });

  const providedBranch = branchName?.trim() ?? '';
  const resolvedBranch =
    providedBranch.length > 0
      ? sanitizeUserProvidedBranchName(providedBranch)
      : buildDefaultRemediationBranchName({
          findingTitle,
          ticketKey,
          brandName,
          timestamp,
        });

  return {
    branchName: resolvedBranch,
    prTitle: resolvedTitle,
  };
}
